/**
 * MergedProductTable - TanStack Table mit Gmail-Checkbox-Pattern
 * Plan 012: Merge Products + Channel Readiness
 *
 * Gmail-Pattern: Checkbox unsichtbar bis Hover; permanent sichtbar sobald ≥1 Zeile ausgewählt.
 * 3 Klick-Pfade: Row→Detail | Badge→Drawer (stopPropagation in Badge) | Button→1-Klick (stopPropagation in Cell)
 */

import { flexRender, type Table as TanStackTable } from '@tanstack/react-table'
import { ArrowUp, ArrowDown } from 'lucide-react'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Checkbox } from '@/components/v2/ui'
import type { ProductWithReadiness } from '@/types/products'
import { DroppableProductRow } from './DroppableProductRow'
import { FetchingOverlay } from './FetchingOverlay'

interface MergedProductTableProps {
  table: TanStackTable<ProductWithReadiness>
  selectedIds: Set<string>
  isFetching?: boolean
  onToggleSelect: (id: string) => void
  onToggleSelectAll: () => void
  onRowClick: (product: ProductWithReadiness) => void
  onFileDrop?: (product: ProductWithReadiness, files: File[]) => void
}

export function MergedProductTable({
  table, selectedIds, isFetching,
  onToggleSelect, onToggleSelectAll, onRowClick, onFileDrop,
}: MergedProductTableProps) {
  const rows = table.getRowModel().rows
  const allSelected = rows.length > 0 && rows.every(r => selectedIds.has(r.original.id))

  return (
    <div className="merged-product-table overflow-x-auto relative">
      {isFetching && <FetchingOverlay />}
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map(hg => (
            <TableRow key={hg.id}>
              <TableHead className="w-8 px-2">
                <Checkbox checked={allSelected} onChange={onToggleSelectAll} aria-label="Alle auswählen" />
              </TableHead>
              {hg.headers.map(header => (
                <TableHead
                  key={header.id}
                  className={`px-2 text-xs ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''}`}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <span className="flex items-center gap-1">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getIsSorted() === 'asc' && <ArrowUp className="h-3 w-3" />}
                    {header.column.getIsSorted() === 'desc' && <ArrowDown className="h-3 w-3" />}
                  </span>
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {rows.map(row => {
            const product = row.original
            const isSelected = selectedIds.has(product.id)
            return (
              <DroppableProductRow
                key={row.id}
                className={`group cursor-pointer ${isSelected ? 'bg-primary-50 dark:bg-primary-950/30' : ''} ${!product.is_active ? 'opacity-50' : ''}`}
                onClick={() => onRowClick(product)}
                onFileDrop={onFileDrop ? (files) => onFileDrop(product, files) : undefined}
              >
                <TableCell className="px-2 py-1.5" onClick={e => e.stopPropagation()}>
                  <Checkbox
                    checked={isSelected}
                    onChange={() => onToggleSelect(product.id)}
                    aria-label={`${product.name} auswählen`}
                  />
                </TableCell>
                {row.getVisibleCells().map(cell => (
                  <TableCell key={cell.id} className="px-2 py-1.5">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </DroppableProductRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
